import { useMemo } from "react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { ResourceHero } from "@/components/site/ResourceHero";
import { glossaryTerms } from "@/data/site/glossary";
import { brand } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";

const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

const Glossary = () => {
  const grouped = useMemo(() => {
    const map = new Map<string, typeof glossaryTerms>();
    [...glossaryTerms]
      .sort((a, b) => a.term.localeCompare(b.term))
      .forEach((t) => {
        const letter = t.term[0].toUpperCase();
        if (!map.has(letter)) map.set(letter, []);
        map.get(letter)!.push(t);
      });
    return Array.from(map.entries());
  }, []);

  const letters = grouped.map(([l]) => l);

  const definedTermSet = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Tax Resolution Glossary",
    url: `${brand.siteUrl}/resources/glossary`,
    hasDefinedTerm: glossaryTerms.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${brand.siteUrl}/resources/glossary`,
      url: `${brand.siteUrl}/resources/glossary#${slugify(t.term)}`,
    })),
  };

  return (
    <SiteLayout>
      <PageSEO
        title="Tax Resolution Glossary — IRS Terms in Plain English"
        description="Plain-language definitions of the IRS and tax-resolution terms you'll hear during your case — from CDP hearings to CSED to Trust Fund Recovery Penalty."
        path="/resources/glossary"
        image={stockImages.accountingDesk.src}
        jsonLd={definedTermSet}
      />
      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "Glossary" },
        ]}
        eyebrow="IRS vocabulary"
        title="Tax resolution glossary"
        summary="The acronyms, form numbers, and IRS jargon you'll hear during a tax resolution case — defined in plain English so you can follow what's actually happening."
        facts={[
          { label: "Terms defined", value: glossaryTerms.length },
          { label: "Format", value: "Plain English" },
          { label: "Updated", value: "2025" },
        ]}
        background="accountingDesk"
      />

      {/* Intro band */}
      <section className="py-10 lg:py-14 bg-cream border-b border-border">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-[17px] leading-[1.75] text-foreground/85">
            The IRS speaks its own language — CSED, CNC, OIC, TFRP, CDP. When you're
            in collections, every one of these acronyms maps to a deadline or a dollar
            figure. This glossary defines{" "}
            <strong className="text-navy">{glossaryTerms.length} terms</strong> in
            plain English so you can read your notices, follow your case, and ask
            sharper questions.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Tip: every term has a permalink — share a definition by clicking the term name.
          </p>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Alphabetical jump nav */}
          <nav aria-label="Jump to letter" className="mb-10 flex flex-wrap gap-1.5 sticky top-20 z-10 bg-white/95 backdrop-blur py-2 -mx-2 px-2 rounded">
            {letters.map((l) => (
              <a
                key={l}
                href={`#letter-${l}`}
                className="w-9 h-9 inline-flex items-center justify-center rounded border border-border text-sm font-bold text-navy hover:bg-gold hover:border-gold hover:text-navy transition-colors"
              >
                {l}
              </a>
            ))}
          </nav>

          <div className="space-y-12">
            {grouped.map(([letter, terms]) => (
              <section key={letter} id={`letter-${letter}`} className="scroll-mt-24">
                <h2 className="font-heading text-3xl font-bold text-gold mb-4 pb-2 border-b border-border">
                  {letter}
                </h2>
                <dl className="divide-y divide-border">
                  {terms.map((t) => {
                    const id = slugify(t.term);
                    return (
                      <div key={t.term} id={id} className="py-5 grid sm:grid-cols-[220px_1fr] gap-3 sm:gap-8 scroll-mt-24">
                        <dt className="font-heading font-bold text-navy text-lg leading-snug">
                          <a href={`#${id}`} className="hover:text-gold">
                            {t.term}
                          </a>
                        </dt>
                        <dd className="text-foreground/80 leading-relaxed">{t.definition}</dd>
                      </div>
                    );
                  })}
                </dl>
              </section>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default Glossary;
